<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <!-- Header Section -->
    <Header />

    <!-- Main Content -->
    <main :class="mainContentClass" class="main-content-wrapper">
      <div class="main-header">
        <h1 class="main-title">Dashboard</h1>
        <div class="header-actions">
          <button
            v-if="isAdmin"
            class="main-btn admin-btn-outline"
            @click="openAddServerModal"
          >
            <vue-feather type="server" size="16" />
            <span
              v-if="!isMobileDevice"
              style="padding-right: 5px; font-size: 12px"
            >
              Add Server</span
            >
          </button>
          <button
            v-if="isAdmin"
            class="main-btn admin-btn-outline"
            @click="openGrantUsageModal"
          >
            <vue-feather type="clock" size="16" />
            <span
              v-if="!isMobileDevice"
              style="padding-right: 5px; font-size: 12px"
            >
              Grant Minutes</span
            >
          </button>
          <button
            v-if="!isAdmin"
            class="main-btn admin-btn-outline"
            @click="openPilotGuide"
          >
            <vue-feather type="book" size="16" />
            <span
              v-if="!isMobileDevice"
              style="padding-right: 5px; font-size: 12px"
            >
              Pilot Guide</span
            >
          </button>
          <button
            class="main-btn"
            :class="{ disabled: isFlightDisabled }"
            :disabled="isFlightDisabled"
            @click="navigateToNewFlight"
          >
            <vue-feather type="plus" size="16" />
            <span
              v-if="!isMobileDevice"
              style="padding-right: 5px; font-size: 12px"
            >
              New Flight</span
            >
          </button>
        </div>
      </div>

      <Divider />
      <br />

      <!-- Dashboard Stats Section -->
      <div class="dashboard-stats">
        <div class="section-header">
          <h2 class="header-title">Flight Statistics</h2>
          <p class="section-description">
            Overview of your flight activity and logs.
          </p>
        </div>

        <div class="stats-cards">
          <div class="stat-card">
            <h2 class="stat-number">{{ totalFlights }}</h2>
            <p class="stat-label">Total Flights</p>
            <vue-feather
              class="stat-icon"
              type="navigation"
              size="24"
            ></vue-feather>
          </div>

          <div
            class="stat-card"
            :class="{ 'server-not-set': !currentStreamingServer }"
          >
            <h2
              class="stat-number stat-number-clickable"
              @click="openServerSelectionModal"
            >
              {{ currentStreamingServer || "Not Set" }}
            </h2>
            <p class="stat-label">
              Streaming Server
              <span
                v-if="currentRTMPUrl"
                class="stat-sublabel rtmp-url-container"
                @click="copyRTMPUrl"
                title="Copy RTMP URL"
              >
                <span class="rtmp-url">{{
                  rtmpUrlCopied ? "Copied!" : currentRTMPUrl
                }}</span>
                <span class="rtmp-copy-icon">
                  <vue-feather
                    :type="rtmpUrlCopied ? 'check' : 'copy'"
                    size="14"
                  />
                </span>
              </span>
            </p>
            <vue-feather
              class="stat-icon"
              type="server"
              size="20"
            ></vue-feather>
          </div>

          <div v-if="usageData" class="stat-card" :class="minutesStatusClass">
            <h2 class="stat-number">
              <span class="stat-value">{{ usageData.remainingMinutes }}</span>
              <span class="stat-divider"> / </span>
              <span class="stat-total">{{
                usageData.totalMinutesAllowed
              }}</span>
              <span class="stat-unit"> min</span>
            </h2>
            <p class="stat-label">Minutes Remaining</p>
            <vue-feather class="stat-icon" type="clock" size="24"></vue-feather>
          </div>

          <div v-if="!usageData" class="stat-card">
            <h2 class="stat-number">
              <span class="stat-value">0</span>
            </h2>
            <p class="stat-label">Minutes Remaining</p>
            <vue-feather class="stat-icon" type="clock" size="24"></vue-feather>
          </div>

          <div
            v-if="usageData && hasActiveSubscription"
            class="stat-card"
            :class="{
              'renewal-urgent': renewalUrgency,
              'subscription-cancelled': !isSubscriptionActive,
            }"
            @click="navigateToSubscriptions"
            style="cursor: pointer"
          >
            <h2 class="stat-number">
              <span class="stat-value">{{ formattedRenewalDate }}</span>
            </h2>
            <p class="stat-label">
              {{ renewalLabel }}
              <span v-if="daysUntilRenewal > 0" class="stat-sublabel">{{
                renewalSubLabel
              }}</span>
            </p>
            <vue-feather
              class="stat-icon"
              type="refresh-cw"
              :size="isMobileDevice ? 18 : 24"
            ></vue-feather>
          </div>

          <div
            v-if="!usageData || !hasActiveSubscription"
            class="stat-card no-subscription"
          >
            <p class="stat-label">No Active Subscription</p>
            <button class="subscribe-btn" @click="navigateToSubscriptions">
              View Plans
            </button>
          </div>
        </div>
      </div>

      <br />
      <Divider />
      <br />

      <h2 class="header-title">Flight Logs</h2>

      <!-- Table -->
      <div class="table-container">
        <div v-if="isLoading" class="loading-state">
          <vue-feather type="loader" size="24" class="spin" />
          <span class="loading-text">Loading your flights...</span>
        </div>
        <div v-else-if="flights.length === 0" class="empty-state">
          <vue-feather type="navigation" size="48" style="color: #aaa" />
          <br />
          <p class="empty-message">You don't have any flights yet.</p>
        </div>
        <div v-else>
          <!-- Flight Logs Table -->
          <div class="flight-table desktop-table">
            <div class="table-header">
              <div class="th name-column">TITLE</div>
              <div class="th date-column">CREATED DATE</div>
              <div class="th location-column">LOCATION</div>
              <div class="th room-column">MEETING LINK</div>
              <div class="th duration-column">DURATION</div>
              <div class="th actions-column">ACTIONS</div>
            </div>
            <div class="table-body">
              <div v-for="flight in flights" :key="flight.id" class="table-row">
                <div class="td name-column">
                  <div class="flight-name">
                    {{ flight.eventName || "Unnamed Flight" }}
                  </div>
                </div>
                <div class="td date-column">
                  <div class="date-info">
                    <span class="date">{{ formatDate(flight.createdAt) }}</span>
                    <span class="time">{{ formatTime(flight.createdAt) }}</span>
                  </div>
                </div>
                <div class="td location-column">
                  <div class="location-info">
                    <span class="location-text">{{
                      flight.location || "N/A"
                    }}</span>
                  </div>
                </div>
                <div class="td room-column">
                  <div
                    class="meeting-link-container"
                    @click="copyMeetingLink(flight)"
                  >
                    <span class="meeting-link-text">{{
                      copiedMeetingId === (flight.id || flight.roomCode)
                        ? "Copied!"
                        : `skyhost.ai/${flight.roomCode || "N/A"}`
                    }}</span>
                    <button
                      class="copy-link-btn"
                      @click.stop="copyMeetingLink(flight)"
                    >
                      <vue-feather
                        :type="
                          copiedMeetingId === (flight.id || flight.roomCode)
                            ? 'check'
                            : 'copy'
                        "
                        size="14"
                      />
                    </button>
                  </div>
                </div>
                <div class="td duration-column">
                  <span class="duration-text">{{
                    formatDuration(flight.durationMinutes)
                  }}</span>
                </div>
                <div class="td actions-column">
                  <span v-if="isLinkExpired(flight)" class="expired-label">
                    Link Expired
                  </span>
                  <button
                    v-else
                    class="action-btn open-btn"
                    :class="{ disabled: isFlightDisabled }"
                    :disabled="isFlightDisabled"
                    @click="openFlight(flight)"
                    title="Open flight in new tab"
                  >
                    <vue-feather type="external-link" size="12" />
                    Flight Portal
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Card Layout -->
          <div class="flight-cards mobile-cards">
            <div
              v-for="flight in flights"
              :key="flight.id"
              class="flight-card-mobile"
            >
              <div class="card-header-mobile">
                <div class="flight-name-mobile">
                  {{ flight.eventName || "Unnamed Flight" }}
                </div>
                <div class="date-info-mobile">
                  <span class="date-mobile">{{
                    formatDate(flight.createdAt)
                  }}</span>
                  <span class="time-mobile">{{
                    formatTime(flight.createdAt)
                  }}</span>
                </div>
              </div>

              <div class="card-content-mobile">
                <div class="location-info-mobile">
                  <vue-feather
                    type="map-pin"
                    size="14"
                    class="location-icon-mobile"
                  />
                  <span class="location-text-mobile">{{
                    flight.location || "N/A"
                  }}</span>
                </div>

                <div
                  class="meeting-link-mobile"
                  @click="copyMeetingLink(flight)"
                >
                  <span class="meeting-link-text-mobile">{{
                    `skyhost.ai/${flight.roomCode || "N/A"}`
                  }}</span>
                  <button
                    class="copy-link-btn-mobile"
                    @click.stop="copyMeetingLink(flight)"
                  >
                    <vue-feather type="copy" size="12" />
                  </button>
                </div>

                <div class="duration-mobile">
                  <span class="duration-text-mobile">{{
                    formatDuration(flight.durationMinutes)
                  }}</span>
                </div>

                <div class="actions-mobile">
                  <span
                    v-if="isLinkExpired(flight)"
                    class="expired-label-mobile"
                  >
                    Link Expired
                  </span>
                  <button
                    v-else
                    class="action-btn-mobile open-btn-mobile"
                    :class="{ disabled: isFlightDisabled }"
                    :disabled="isFlightDisabled"
                    @click="openFlight(flight)"
                    title="Open flight in new tab"
                  >
                    <vue-feather type="external-link" size="12" />
                    Flight Portal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Add Server Modal -->
    <AddServerModal
      :isOpen="isAddServerModalOpen"
      @close="closeAddServerModal"
      @server-created="handleServerCreated"
    />

    <!-- Grant Usage Modal -->
    <GrantUsageModal
      :isOpen="isGrantUsageModalOpen"
      @close="closeGrantUsageModal"
      @usage-granted="handleUsageGranted"
    />

    <!-- Server Selection Modal -->
    <ServerSelectionModal
      :isOpen="isServerSelectionModalOpen"
      :currentServerId="currentStreamingServerId"
      @close="closeServerSelectionModal"
      @server-assigned="handleServerAssigned"
    />
  </div>
</template>

<script>
import { auth, db } from "@/plugins/firebase";
import {
  getFirestore,
  collection,
  query,
  onSnapshot,
  orderBy,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Divider from "@/components/organisms/Divider.vue";
import AddServerModal from "@/components/organisms/modal/AddServerModal.vue";
import GrantUsageModal from "@/components/organisms/modal/GrantUsageModal.vue";
import ServerSelectionModal from "@/components/organisms/modal/ServerSelectionModal.vue";
import { DateTime } from "luxon";
import { subscribeToUserStreamingUrls } from "@/services/streamingService";

export default {
  data() {
    return {
      flights: [],
      isLoading: true,
      totalFlights: 0,
      activeFlights: 0,
      completedFlights: 0,
      recentFlights: 0,
      totalUsageMinutes: 0,
      usageData: null,
      unsubscribeUsage: null,
      unsubscribeStreaming: null,
      isAdmin: false,
      isAddServerModalOpen: false,
      isGrantUsageModalOpen: false,
      isServerSelectionModalOpen: false,
      currentUser: null,
      currentStreamingServer: null,
      currentStreamingServerId: null,
      currentRTMPUrl: null,
      rtmpUrlCopied: false,
      copiedMeetingId: null,
    };
  },
  components: {
    Header,
    Divider,
    AddServerModal,
    GrantUsageModal,
    ServerSelectionModal,
  },
  computed: {
    daysUntilRenewal() {
      if (!this.usageData || !this.usageData.billingPeriodEnd) {
        return 0;
      }
      const endDate = this.usageData.billingPeriodEnd.toDate
        ? this.usageData.billingPeriodEnd.toDate()
        : new Date(this.usageData.billingPeriodEnd);
      const now = new Date();
      const diffTime = endDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },
    recentMinutes() {
      const now = new Date();
      const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
      return this.flights
        .filter((flight) => {
          if (!flight.createdAt) return false;
          const createdDate = flight.createdAt.toDate
            ? flight.createdAt.toDate()
            : new Date(flight.createdAt);
          return createdDate >= sevenDaysAgo;
        })
        .reduce((sum, flight) => sum + (flight.durationMinutes || 0), 0);
    },
    minutesPercentage() {
      if (!this.usageData || !this.usageData.totalMinutesAllowed) return 0;
      return (
        (this.usageData.remainingMinutes / this.usageData.totalMinutesAllowed) *
        100
      );
    },
    minutesStatusClass() {
      const percent = this.minutesPercentage;
      if (percent > 50) return "status-good";
      if (percent >= 25) return "status-warning";
      return "status-low";
    },
    planLabel() {
      if (!this.usageData) return "";
      return this.usageData.planName || "Plan";
    },
    formattedRenewalDate() {
      if (!this.usageData || !this.usageData.billingPeriodEnd) {
        return "";
      }
      const endDate = this.usageData.billingPeriodEnd.toDate
        ? this.usageData.billingPeriodEnd.toDate()
        : new Date(this.usageData.billingPeriodEnd);
      return endDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
    renewalUrgency() {
      return this.daysUntilRenewal > 0 && this.daysUntilRenewal < 7;
    },
    isFlightDisabled() {
      // Disable button if:
      // 1. No usage data exists (new account with no plan)
      // 2. remainingMinutes is undefined
      // 3. remainingMinutes is less than 1
      return (
        !this.usageData ||
        this.usageData.remainingMinutes === undefined ||
        this.usageData.remainingMinutes < 1
      );
    },
    isSubscriptionActive() {
      return this.usageData?.status === "active";
    },
    hasActiveSubscription() {
      // Check if user has an actual subscription (not just admin grant)
      // Admin grants have null billingPeriodEnd and/or subscriptionId
      return (
        this.usageData &&
        this.usageData.billingPeriodEnd !== null &&
        this.usageData.subscriptionId !== null
      );
    },
    renewalLabel() {
      return this.isSubscriptionActive ? "Renewal Date" : "Plan Expires";
    },
    renewalSubLabel() {
      if (!this.daysUntilRenewal) return "";
      return this.isSubscriptionActive
        ? `${this.daysUntilRenewal} days until renewal`
        : `${this.daysUntilRenewal} days remaining`;
    },
  },
  methods: {
    async fetchUserData() {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const db = getFirestore();
        const usersQuery = query(
          collection(db, "users"),
          where("userId", "==", userId)
        );
        const querySnapshot = await getDocs(usersQuery);
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          this.currentUser = userData;
          this.isAdmin = userData.isAdmin === true;
        }

        // Setup streaming config listener
        this.setupStreamingListener();
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },
    setupStreamingListener() {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      // Clean up existing listener if any
      if (this.unsubscribeStreaming) {
        this.unsubscribeStreaming();
      }

      // Subscribe to real-time updates
      this.unsubscribeStreaming = subscribeToUserStreamingUrls(
        userId,
        (streamingUrls) => {
          if (streamingUrls && streamingUrls.server) {
            this.currentStreamingServer = streamingUrls.server.name;
            this.currentStreamingServerId = streamingUrls.server.id;
            this.currentRTMPUrl = streamingUrls.rtmpUrl;
          } else {
            this.currentStreamingServer = null;
            this.currentStreamingServerId = null;
            this.currentRTMPUrl = null;
          }
        }
      );
    },
    openServerSelectionModal() {
      this.isServerSelectionModalOpen = true;
    },
    closeServerSelectionModal() {
      this.isServerSelectionModalOpen = false;
    },
    async handleServerAssigned() {
      // No need to manually refresh - onSnapshot will handle it automatically
    },
    openAddServerModal() {
      this.isAddServerModalOpen = true;
    },
    closeAddServerModal() {
      this.isAddServerModalOpen = false;
    },
    handleServerCreated() {
      // Server created successfully - modal will close automatically
      // You can add any additional logic here if needed
    },
    openGrantUsageModal() {
      this.isGrantUsageModalOpen = true;
    },
    closeGrantUsageModal() {
      this.isGrantUsageModalOpen = false;
    },
    handleUsageGranted() {
      // Usage granted successfully - modal will close automatically
      // The usage listener will automatically update the UI
    },
    navigateToNewFlight() {
      window.open("/new-flight", "_blank");
    },
    openPilotGuide() {
      window.open("/blog/pilot-playbook", "_blank");
    },
    navigateToSubscriptions() {
      this.$router.push("/subscriptions");
    },
    async fetchFlights() {
      try {
        this.isLoading = true;
        const userId = auth.currentUser.uid;
        const db = getFirestore();

        console.log("userId>>>", userId);

        const flightsQuery = query(
          collection(db, "flights"),
          where("creatorUserId", "==", userId),
          orderBy("createdAt", "desc")
        );

        this.unsubscribe = onSnapshot(
          flightsQuery,
          (snapshot) => {
            try {
              const flightsData = [];

              snapshot.docs.forEach((docSnapshot) => {
                const flight = {
                  id: docSnapshot.id,
                  ...docSnapshot.data(),
                };
                flightsData.push(flight);
              });

              this.flights = flightsData;
              this.updateFlightStats();
            } catch (snapshotError) {
              console.error("Error processing snapshot:", snapshotError);
            } finally {
              this.isLoading = false;
            }
          },
          (error) => {
            console.error("Snapshot listener error:", error);
            this.isLoading = false;
          }
        );
      } catch (error) {
        console.error("Error setting up flight listener:", error);
        this.isLoading = false;
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      try {
        const date = timestamp.toDate
          ? timestamp.toDate()
          : new Date(timestamp);
        return DateTime.fromJSDate(date).toFormat("MMM dd, yyyy");
      } catch (error) {
        console.error("Error formatting date:", error);
        return "";
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      try {
        const date = timestamp.toDate
          ? timestamp.toDate()
          : new Date(timestamp);
        return DateTime.fromJSDate(date).toFormat("hh:mm a");
      } catch (error) {
        console.error("Error formatting time:", error);
        return "";
      }
    },
    getFlightStatus(flight) {
      // Simple status based on createdAt
      // Active: created within last 24 hours
      // Completed: older than 24 hours
      if (!flight.createdAt) return "scheduled";

      try {
        const createdDate = flight.createdAt.toDate
          ? flight.createdAt.toDate()
          : new Date(flight.createdAt);
        const now = new Date();
        const hoursSinceCreation = (now - createdDate) / (1000 * 60 * 60);

        if (hoursSinceCreation <= 24) {
          return "active";
        } else {
          return "completed";
        }
      } catch (error) {
        console.error("Error determining flight status:", error);
        return "scheduled";
      }
    },
    isLinkExpired(flight) {
      if (!flight.createdAt) return false;
      try {
        const createdDate = flight.createdAt.toDate
          ? flight.createdAt.toDate()
          : new Date(flight.createdAt);
        const now = new Date();
        const hoursSinceCreation = (now - createdDate) / (1000 * 60 * 60);
        return hoursSinceCreation > 24;
      } catch (error) {
        console.error("Error checking link expiration:", error);
        return false;
      }
    },
    copyMeetingLink(flight) {
      const baseUrl = window.location.origin;
      const flightUrl = `${baseUrl}/${flight.roomCode}`;
      navigator.clipboard.writeText(flightUrl).then(
        () => {
          this.copiedMeetingId = flight.id || flight.roomCode || null;
          setTimeout(() => {
            this.copiedMeetingId = null;
          }, 1500);
        },
        (err) => {
          console.error("Could not copy text: ", err);
        }
      );
    },
    copyRTMPUrl() {
      if (this.currentRTMPUrl && !this.rtmpUrlCopied) {
        navigator.clipboard.writeText(this.currentRTMPUrl).then(
          () => {
            this.rtmpUrlCopied = true;
            setTimeout(() => {
              this.rtmpUrlCopied = false;
            }, 2000);
          },
          (err) => {
            console.error("Could not copy text: ", err);
          }
        );
      }
    },
    openFlight(flight) {
      const baseUrl = window.location.origin;
      const flightUrl = `${baseUrl}/${flight.roomCode}`;
      window.open(flightUrl, "_blank");
    },
    formatDuration(minutes) {
      if (!minutes || minutes === 0) return "0 min";
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
      }
      return `${mins} min`;
    },
    formatTotalUsageValue(minutes) {
      if (!minutes || minutes === 0) return "0";
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return mins > 0 ? `${hours} hr ${mins}` : `${hours}`;
      }
      return `${mins}`;
    },
    formatTotalUsageUnit(minutes) {
      if (!minutes || minutes === 0) return "min";
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return mins > 0 ? " min" : " hr";
      }
      return " min";
    },
    updateFlightStats() {
      this.totalFlights = this.flights.length;

      const now = new Date();
      const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);

      this.activeFlights = this.flights.filter((flight) => {
        if (!flight.createdAt) return false;
        const createdDate = flight.createdAt.toDate
          ? flight.createdAt.toDate()
          : new Date(flight.createdAt);
        const hoursSinceCreation = (now - createdDate) / (1000 * 60 * 60);
        return hoursSinceCreation <= 24;
      }).length;

      this.completedFlights = this.flights.filter((flight) => {
        if (!flight.createdAt) return false;
        const createdDate = flight.createdAt.toDate
          ? flight.createdAt.toDate()
          : new Date(flight.createdAt);
        const hoursSinceCreation = (now - createdDate) / (1000 * 60 * 60);
        return hoursSinceCreation > 24;
      }).length;

      this.recentFlights = this.flights.filter((flight) => {
        if (!flight.createdAt) return false;
        const createdDate = flight.createdAt.toDate
          ? flight.createdAt.toDate()
          : new Date(flight.createdAt);
        return createdDate >= sevenDaysAgo;
      }).length;

      this.totalUsageMinutes = this.flights.reduce((sum, flight) => {
        return sum + (flight.durationMinutes || 0);
      }, 0);
    },
    setupUsageListener() {
      if (this.unsubscribeUsage) {
        this.unsubscribeUsage();
      }

      const userId = auth.currentUser?.uid;
      if (!userId) {
        this.usageData = null;
        return;
      }

      const usageQuery = query(
        collection(db, "subscription-usage"),
        where("userId", "==", userId),
        where("isCurrentUsage", "==", true),
        limit(1)
      );

      this.unsubscribeUsage = onSnapshot(usageQuery, (snapshot) => {
        if (snapshot.empty) {
          this.usageData = null;
          return;
        }
        const doc = snapshot.docs[0];
        const data = doc.data();

        // Show usage data regardless of remaining minutes or period status
        // This allows users to see their usage even after plan expires or is cancelled
        this.usageData = {
          id: doc.id,
          ...data,
        };
      });
    },
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    });

    const isMobileDevice = computed(() => isMobile());

    return {
      mainContentClass,
      isMobileDevice,
    };
  },
  mounted() {
    this.fetchFlights();
    this.setupUsageListener();
    this.fetchUserData();
  },
  beforeUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.unsubscribeUsage) {
      this.unsubscribeUsage();
    }
    if (this.unsubscribeStreaming) {
      this.unsubscribeStreaming();
    }
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  width: 100%;
  overflow-x: hidden;
}

/* Main Content */
.mobile-main-content {
  margin-top: 0;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
}

.main-content-wrapper {
  width: 100%;
  box-sizing: border-box;
}

.web-main-content {
  padding: 8rem 10rem;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.main-title {
  color: #000;
  margin-bottom: 1rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 4rem;
  font-weight: 700;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.main-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  height: 48px;
}

.main-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.admin-btn {
  background-color: #6366f1;
}

.admin-btn:hover:not(:disabled) {
  background-color: #4f46e5;
}

.admin-btn-outline {
  background-color: transparent;
  color: #000;
  border: 1px solid #d1d5db;
}

.admin-btn-outline:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #000;
}

.admin-btn-outline :deep(svg) {
  color: #000;
  stroke: #000;
}

.main-btn.disabled,
.main-btn:disabled {
  background-color: #9ca3af;
  color: #000;
  cursor: not-allowed;
  opacity: 0.6;
}

.main-btn span {
  display: inline-block;
  line-height: 1;
}

/* Table Styles */
.table-container {
  margin-top: 2rem;
  background-color: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  padding: 20px;
  background-color: #f9fafb;
}

.empty-message {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.flight-table {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  background-color: #f9fafb;
  border-bottom: 1px solid #eee;
  padding: 1rem 1.5rem;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #eee;
  align-items: center;
  padding: 0.75rem 1.5rem;
}

.table-row:hover {
  background-color: #f9fafb;
}

.th,
.td {
  font-size: 0.875rem;
  padding: 0.5rem;
}

.th {
  font-weight: 600;
  color: #4b5563;
  font-size: 0.813rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

/* Column width distribution */
.name-column {
  width: 20%;
  flex: 0 0 20%;
}

.date-column {
  width: 13%;
  flex: 0 0 13%;
}

.location-column {
  width: 22%;
  flex: 0 0 22%;
}

.room-column {
  width: 20%;
  flex: 0 0 20%;
}

.duration-column {
  width: 12%;
  flex: 0 0 12%;
}

.actions-column {
  width: 13%;
  flex: 0 0 13%;
  display: flex;
  gap: 8px;
}

/* Responsive table adjustments */
@media (max-width: 1024px) {
  .name-column {
    width: 30%;
    flex: 0 0 30%;
  }

  .date-column {
    width: 18%;
    flex: 0 0 18%;
  }

  .location-column {
    width: 30%;
    flex: 0 0 30%;
  }

  .room-column {
    width: 15%;
    flex: 0 0 15%;
  }

  .duration-column {
    width: 11%;
    flex: 0 0 11%;
  }

  .actions-column {
    width: 16%;
    flex: 0 0 16%;
  }
}

@media (max-width: 900px) {
  .date-column {
    display: none;
  }

  .name-column {
    width: 35%;
    flex: 0 0 35%;
  }

  .location-column {
    width: 35%;
    flex: 0 0 35%;
  }

  .room-column {
    width: 18%;
    flex: 0 0 18%;
  }

  .duration-column {
    width: 12%;
    flex: 0 0 12%;
  }

  .actions-column {
    width: 15%;
    flex: 0 0 15%;
  }
}

/* Column specific styles */
.flight-name {
  font-weight: 700;
  color: #111827;
  font-size: 1rem;
}

.date-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
}

.time {
  font-size: 0.75rem;
  color: #6b7280;
}

.location-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.location-icon {
  color: #6b7280;
}

.location-text {
  font-size: 0.875rem;
  color: #111827;
}

.meeting-link-container {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.meeting-link-container:hover {
  border-color: #d1d5db;
}

.meeting-link-text {
  flex: 1;
  padding: 0.5rem 0.75rem;
  font-family: "Courier New", monospace;
  font-size: 0.813rem;
  color: #111827;
  border: none;
  background: transparent;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-link-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border: none;
  border-left: 1px solid #e5e7eb;
  border-radius: 0 6px 6px 0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-link-btn:hover {
  background-color: #e5e7eb;
}

.duration-text {
  font-size: 0.875rem;
  color: #111827;
  font-weight: 500;
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.status-badge.active {
  background-color: #dbeafe;
  color: #2563eb;
}

.status-badge.completed {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-badge.scheduled {
  background-color: #fef3c7;
  color: #d97706;
}

.action-btn {
  padding: 0.75rem 1rem;
  font-size: 0.7rem;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-weight: 500;
}

.open-btn {
  background-color: #000;
  color: white;
}

.open-btn:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.action-btn.disabled,
.action-btn:disabled,
.open-btn.disabled,
.open-btn:disabled {
  background-color: #9ca3af;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

.expired-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  /* padding: 0.5rem 0.75rem; */
}

.expired-label-mobile {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  padding: 8px 12px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  gap: 1rem;
  color: #6b7280;
}

.loading-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.spin {
  animation: spin 1s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Dashboard Stats */
.dashboard-stats {
  margin-bottom: 3rem;
}

.section-header {
  margin-bottom: 32px;
}

.section-description {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
  margin-top: 8px;
}

.header-title {
  font-size: 1.5rem;
  color: #000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.stats-cards {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  width: 100%;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  flex: 1;
  position: relative;
  border: 2px solid #f2f5f9;
  transition: all 0.3s ease;
}

.stat-card:hover {
  border-color: #e5e7eb;
}

.stat-card.no-subscription {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 24px;
}

.stat-card-clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.subscribe-btn {
  background-color: #000;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 15px 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.subscribe-btn:hover {
  background-color: #333;
}

.stat-number {
  font-size: 45px;
  margin: 0;
  color: #000;
  font-weight: 300;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 5px;
  padding-right: 40px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.stat-value {
  font-size: inherit;
  font-weight: inherit;
}

.stat-unit {
  font-size: 0.5em;
  font-weight: 400;
  opacity: 0.8;
}

.stat-label {
  color: #6b7280;
  margin: 8px 0;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-sublabel {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 400;
}

.stat-number-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: inline-block;
  width: fit-content;
}

.stat-number-clickable:hover {
  color: #3b82f6;
}

.stat-number-clickable::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: #3b82f6;
  transition: width 0.3s ease;
}

.stat-number-clickable:hover::after {
  width: 100%;
}

.rtmp-url-container {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.rtmp-url-container:hover .rtmp-url {
  color: #000;
}

.rtmp-url {
  flex: 1;
}

.rtmp-copy-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.2s ease;
}

.rtmp-url-container:hover .rtmp-copy-icon {
  color: #000;
}

.stat-divider {
  font-size: 0.6em;
  color: #9ca3af;
  margin: 0 2px;
}

.stat-total {
  font-size: 0.6em;
  color: #9ca3af;
  font-weight: 300;
}

.stat-card.status-good .stat-value {
  color: #10b981;
}

.stat-card.status-warning .stat-value {
  color: #f59e0b;
}

.stat-card.status-low .stat-value {
  color: #ef4444;
}

.stat-card.renewal-urgent {
  border-color: #f59e0b;
  background-color: #fffbeb;
}

.stat-card.renewal-urgent:hover {
  border-color: #f59e0b;
  background-color: #fef3c7;
}

.stat-card.subscription-cancelled {
  border-color: #fca5a5;
  /* background-color: #fef2f2; */
}

.stat-card.subscription-cancelled:hover {
  border-color: #fca5a5;
}

.stat-card.server-not-set {
  border-color: #fca5a5;
}

.stat-card.server-not-set:hover {
  border-color: #fca5a5;
}

.stat-icon {
  position: absolute;
  top: 24px;
  right: 24px;
  color: #6b7280;
}

/* Responsive */
@media (max-width: 768px) {
  .app-container {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }

  .mobile-main-content {
    width: 100%;
    max-width: 100%;
    padding: 1rem;
    margin-top: 0;
    box-sizing: border-box;
  }

  .main-header {
    margin-top: 5rem;
  }

  .main-title {
    font-size: 2rem;
  }

  .stats-cards {
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  .stat-card {
    padding: 24px;
    min-width: unset;
    max-width: 100%;
    width: 100%;
  }

  .stat-number {
    font-size: 40px;
    padding-right: 45px;
    word-break: break-word;
    line-height: 1.2;
    overflow-wrap: break-word;
    max-width: calc(100% - 45px);
  }

  .stat-unit {
    font-size: 0.5em;
  }

  .stat-icon {
    top: 20px;
    right: 20px;
  }

  .stat-label {
    font-size: 13px;
  }

  .stat-sublabel {
    font-size: 11px;
  }

  .rtmp-url-container {
    flex-direction: row;
    gap: 8px;
  }

  .rtmp-url {
    font-size: 10px;
    line-height: 1.4;
  }

  .rtmp-copy-icon {
    min-width: 24px;
    min-height: 24px;
  }

  .stat-number-clickable {
    max-width: calc(100% - 45px);
  }

  .stat-divider,
  .stat-total {
    font-size: 0.55em;
  }

  .web-main-content {
    padding: 2rem;
  }

  /* Hide desktop table on mobile */
  .desktop-table {
    display: none;
  }

  /* Show mobile cards */
  .mobile-cards {
    display: block;
  }

  .flight-cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .flight-card-mobile {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .card-header-mobile {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .flight-name-mobile {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    flex: 1;
    margin-right: 12px;
  }

  .date-info-mobile {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: right;
  }

  .date-mobile {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
  }

  .time-mobile {
    font-size: 12px;
    color: #6b7280;
  }

  .card-content-mobile {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .location-info-mobile {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #f9fafb;
    border-radius: 8px;
  }

  .location-icon-mobile {
    color: #6b7280;
    flex-shrink: 0;
  }

  .location-text-mobile {
    font-size: 14px;
    color: #111827;
    line-height: 1.3;
  }

  .meeting-link-mobile {
    display: flex;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background-color: #fff;
    cursor: pointer;
    transition: all 0.2s;
  }

  .meeting-link-mobile:hover {
    border-color: #d1d5db;
  }

  .meeting-link-text-mobile {
    flex: 1;
    padding: 8px 12px;
    font-family: "Courier New", monospace;
    font-size: 13px;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy-link-btn-mobile {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f4f6;
    border: none;
    border-left: 1px solid #e5e7eb;
    border-radius: 0 6px 6px 0;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .copy-link-btn-mobile:hover {
    background-color: #e5e7eb;
  }

  .duration-mobile {
    padding: 8px 12px;
    background-color: #f3f4f6;
    border-radius: 6px;
    text-align: center;
  }

  .duration-text-mobile {
    font-size: 13px;
    color: #111827;
    font-weight: 500;
  }

  .actions-mobile {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .action-btn-mobile {
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
  }

  .open-btn-mobile {
    background-color: #000;
    color: white;
  }

  .open-btn-mobile:hover:not(:disabled) {
    background-color: #1a1a1a;
  }

  .action-btn-mobile.disabled,
  .action-btn-mobile:disabled,
  .open-btn-mobile.disabled,
  .open-btn-mobile:disabled {
    background-color: #9ca3af;
    color: #6b7280;
    cursor: not-allowed;
    opacity: 0.6;
  }
}

/* Hide mobile cards on desktop */
@media (min-width: 769px) {
  .mobile-cards {
    display: none;
  }
}
</style>
